import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import {
  formatLogDataPretty,
  getLogDisplayText,
  type DispatchLogEvent,
} from '../../types/dispatch';

interface LogsConsoleProps {
  logs: DispatchLogEvent[];
  levelFilter: string;
  searchFilter: string;
  onLevelChange: (level: string) => void;
  onSearchChange: (value: string) => void;
}

const LEVEL_COLORS: Record<string, string> = {
  INFO: '#6b7280',
  DEBUG: '#9ca3af',
  WARNING: '#f59e0b',
  ERROR: '#ef4444',
};

function formatTs(ts: string): string {
  try {
    const d = new Date(ts);
    const y = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const m = String(d.getMinutes()).padStart(2, '0');
    const s = String(d.getSeconds()).padStart(2, '0');
    const ms = String(d.getMilliseconds()).padStart(3, '0');
    return `${y}-${mo}-${day} ${h}:${m}:${s}.${ms}`;
  } catch {
    return ts;
  }
}

const LogsConsole: React.FC<LogsConsoleProps> = ({
  logs,
  levelFilter,
  searchFilter,
  onLevelChange,
  onSearchChange,
}) => {
  const filtered = logs.filter((l) => {
    if (levelFilter && l.level !== levelFilter) return false;
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      const text = getLogDisplayText(l).toLowerCase();
      const logger = (l.logger || '').toLowerCase();
      if (!text.includes(search) && !logger.includes(search)) return false;
    }
    return true;
  });

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dispatch Logs (Run Level)
      </Typography>
      <Box display="flex" gap={1} sx={{ mb: 1 }}>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Level</InputLabel>
          <Select
            value={levelFilter}
            label="Level"
            onChange={(e: { target: { value: string } }) => onLevelChange(e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="INFO">INFO</MenuItem>
            <MenuItem value="DEBUG">DEBUG</MenuItem>
            <MenuItem value="WARNING">WARNING</MenuItem>
            <MenuItem value="ERROR">ERROR</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder="Search"
          value={searchFilter}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          sx={{ flex: 1, maxWidth: 200 }}
        />
      </Box>
      <Paper
        variant="outlined"
        sx={{
          p: 1,
          minHeight: 320,
          maxHeight: '70vh',
          overflow: 'auto',
          fontFamily: 'monospace',
          fontSize: '0.8rem',
          bgcolor: '#1e1e1e',
          color: '#d4d4d4',
        }}
      >
        {filtered.length === 0 ? (
          <Box color="gray">No logs</Box>
        ) : (
          filtered.map((log) => (
            <Box
              key={log.id}
              sx={{
                py: 0.25,
                borderBottom: '1px solid rgba(255,255,255,0.05)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' },
              }}
            >
              <span style={{ color: '#6b7280' }}>[{formatTs(log.ts)}]</span>{' '}
              <span
                style={{
                  color: LEVEL_COLORS[log.level] || '#9ca3af',
                  fontWeight: log.level === 'ERROR' ? 600 : 400,
                }}
              >
                {log.level}
              </span>{' '}
              {log.logger && (
                <span style={{ color: '#60a5fa' }}>{log.logger}</span>
              )}{' '}
              {log.message}
              {log.data != null && (
                <Box
                  component="pre"
                  sx={{
                    mt: 0.5,
                    ml: 1,
                    mb: 0,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'rgba(0,0,0,0.2)',
                    color: '#9ca3af',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflow: 'auto',
                    maxHeight: 320,
                  }}
                >
                  {formatLogDataPretty(log.data)}
                </Box>
              )}
              {log.exception && (
                <Box
                  component="details"
                  sx={{ mt: 0.5, ml: 2, color: '#f87171' }}
                >
                  <summary>Exception</summary>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(log.exception, null, 2)}
                  </pre>
                </Box>
              )}
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default LogsConsole;
